import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, PenLine } from "lucide-react";
import { toast } from "sonner";

import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import MarkdownEditor from "../advisor/common/MarkdownEditor";
import { fetchDashboardNote, saveDashboardNote } from "@/data/dashboard-note";
import { apiError } from "@/lib/api-error";

export default function DashboardNoteSection({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const note = useQuery({ queryKey: ["dashboard-note"], queryFn: fetchDashboardNote });
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState("");

  const save = useMutation({
    mutationFn: () => saveDashboardNote(draft),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboard-note"] });
      setEditing(false);
      toast.success("Saved. Everyone in the organisation sees it on their dashboard.");
    },
    onError: (error) => toast.error(apiError(error, "Could not save the note.")),
  });

  if (note.isLoading) return null;
  const markdown = note.data?.markdown ?? "";
  if (!isAdmin && !markdown.trim()) return null;

  const startEditing = () => {
    setDraft(markdown);
    setEditing(true);
  };

  return (
    <Card className="relative mt-6" data-testid="dashboard-note">
      {isAdmin && !editing && (
        <Button variant="ghost" size="sm" onClick={startEditing} className="absolute right-3 top-3 text-muted-foreground" data-testid="dashboard-note-edit">
          <PenLine className="mr-1.5 h-3.5 w-3.5" />
          {markdown.trim() ? "Edit" : "Write a note"}
        </Button>
      )}
      <CardContent className="pt-6">
        {editing ? (
          <div className="space-y-3" data-testid="dashboard-note-editor">
            <MarkdownEditor value={draft} onChange={setDraft} height="max(60vh, 480px)" />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditing(false)} disabled={save.isPending}>Cancel</Button>
              <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="dashboard-note-save">
                {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save
              </Button>
            </div>
          </div>
        ) : markdown.trim() ? (
          <div data-testid="dashboard-note-content">
            <MarkdownEditor value={markdown} showMenu={false} disabled height="auto" />
          </div>
        ) : (
          <p className="pr-32 text-sm text-muted-foreground" data-testid="dashboard-note-empty">
            Nothing here yet. Write a welcome, the house rules, or where to find things; everyone in the organisation sees it here, and only admins can change it.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
